// frontend/src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Banknote, Package, FileText, User, Home as HomeIcon,
  ArrowRightLeft, Shield, LogOut, MessageCircle, Users, FileCog, Wallet, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import persianDate from 'persian-date';

import GoldChart from '../components/GoldChart';

function NavCard({ icon, label, link, color = 'indigo', delay = 0 }) {
  const colorClasses = {
    indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700',
    emerald: 'from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700',
    purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
    amber: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
    rose: 'from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, type: "spring", stiffness: 120 }}
      whileHover={{ scale: 1.08, y: -4 }}
      whileTap={{ scale: 0.95 }}
    >
      <Link
        to={link}
        className={`relative overflow-hidden bg-gradient-to-br ${colorClasses[color]} text-white rounded-3xl shadow-xl p-6 flex flex-col items-center justify-center transition-all duration-300 group border border-white/20`}
      >
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
        <motion.div
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.6 }}
          className="p-4 bg-white bg-opacity-20 rounded-full backdrop-blur-sm"
        >
          {icon}
        </motion.div>
        <span className="mt-3 text-sm font-bold tracking-wide">{label}</span>
        <Sparkles className="absolute top-2 right-2 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
    </motion.div>
  );
}

export default function Home() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = () => {
      const token = localStorage.getItem('authToken');
      const userInfo = localStorage.getItem('userInfo');

      if (!token || !userInfo) {
        setLoading(false);
        return;
      }

      try {
        const parsed = JSON.parse(userInfo);
        setUser({
          id: parsed.id,
          first_name: parsed.name || parsed.first_name || 'کاربر',
          is_admin: parsed.is_admin === 1 || parsed.is_admin === true
        });
      } catch (err) {
        console.error('خطا در پارس userInfo', err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [navigate]);

  // --- همیشه SEO رو صدا بزن ---
  const seoProps = {
    title: "خرید و فروش آبشده انگدار | طلای آب شده معتبر اصفهان | ESF Gold",
    description: "خرید و فروش آبشده انگدار معتبر در اصفهان با آزمایشگاه طلا و ری‌گیری دقیق. آبشده 750، شمش طلا، خرید آبشده وزن کم با کمترین کارمزد و تحویل فوری در esf.gold",
    keywords: "خرید آبشده, فروش آبشده, آبشده انگدار, آبشده معتبر, طلای آب شده اصفهان, آزمایشگاه طلا, ری گیری طلا, آبشده 750, خرید شمش طلا, آبشده وزن کم, esf gold",
    ogImage: "https://esf.gold/og-home.jpg",
    url: "https://esf.gold/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ESF Gold",
      "url": "https://esf.gold",
      "logo": "https://esf.gold/logo.png",
      "description": "خرید و فروش آبشده انگدار معتبر در اصفهان با آزمایشگاه طلا و ری‌گیری دقیق",
      "address": { "@type": "PostalAddress", "addressLocality": "اصفهان", "addressCountry": "IR" },
      "contactPoint": { "@type": "ContactPoint", "contactType": "customer service" }
    }
  };

  // --- لودینگ ---
  if (loading) {
    return (
      <>
        
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full shadow-lg"
          />
        </div>
      </>
    );
  }

  // --- بدون لاگین ---
  if (!user) {
    return (
      <>
      
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-12 text-center max-w-md border border-white/30"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mx-auto mb-6 flex items-center justify-center"
            >
              <HomeIcon className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">خوش آمدید</h1>
            <p className="text-gray-600 mb-8">لطفاً وارد حساب کاربری خود شوید.</p>
            <Link
              to="/Login_page"
              className="inline-block bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              ورود به حساب
            </Link>
          </motion.div>
        </div>
      </>
    );
  }

  const isAdmin = user.is_admin;

  return (
    <>
  
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-6 font-vazir">
        
        {/* هدر خوش‌آمدگویی */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 80 }}
          className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 mb-6 border border-white/30"
        >
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 flex items-center gap-3">
                خوش آمدید، {user.first_name}
                {isAdmin && (
                  <motion.div
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Shield className="w-9 h-9 text-yellow-500 drop-shadow-md" />
                  </motion.div>
                )}
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                {isAdmin ? 'مدیریت کامل سیستم در دستان شماست.' : 'به پنل کاربری خوش آمدید.'}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 15 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                localStorage.clear();
                navigate('/Login_page');
              }}
              className="bg-gradient-to-r from-red-500 to-rose-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all"
              title="خروج"
            >
              <LogOut className="w-6 h-6" />
            </motion.button>
          </div>
        </motion.div>

        {/* کارت‌های ناوبری */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
          <NavCard icon={<Banknote className="w-8 h-8" />} label="واریز / برداشت" link="/deposit-withdraw_page" delay={0.1} />
          <NavCard icon={<ArrowRightLeft className="w-8 h-8" />} label="خرید و فروش" link="/trade_page" delay={0.2} />
          <NavCard icon={<FileText className="w-8 h-8" />} label="تراکنش‌ها" link="/transactions_page" delay={0.3} />
          <NavCard icon={<Package className="w-8 h-8" />} label="درخواست آبشده" link="/gold-request_page" delay={0.4} />
          <NavCard icon={<Wallet className="w-8 h-8" />} label="کیف پول" link="/wallet-requests_page" delay={0.5} />
          <NavCard icon={<User className="w-8 h-8" />} label="پروفایل" link="/profile_page" delay={0.6} />

          <AnimatePresence>
            {isAdmin && (
              <>
                <NavCard icon={<Users className="w-8 h-8" />} label="درجه کاربران" link="/admin-grades_page" color="emerald" delay={0.8} />
                <NavCard icon={<Package className="w-8 h-8" />} label="درخواست‌های آبشده" link="/admin-gold-requests_page" color="rose" delay={1.0} />
                <NavCard icon={<Wallet className="w-8 h-8" />} label="درخواست کیف پول" link="/AdminWalletRequests_page" color="indigo" delay={1.1} />
                <NavCard icon={<User className="w-8 h-8" />} label="موجودی کاربران" link="/AdminUserBalances_page" color="purple" delay={1.2} />
                <NavCard icon={<ArrowRightLeft className="w-8 h-8" />} label="تراکنش طلا" link="/AdminGoldTransactions_page" color="emerald" delay={1.3} />
                <NavCard icon={<Banknote className="w-8 h-8" />} label="شارژ حساب" link="/AdminChargeAccount_page" color="amber" delay={1.4} />
                <NavCard icon={<FileCog className="w-8 h-8" />} label="تنظیمات قیمت" link="/AdminPriceSettings_page" color="rose" delay={1.5} />
                <NavCard icon={<MessageCircle className="w-8 h-8" />} label="چت پشتیبانی" link="/admin-chat_page" color="indigo" delay={1.6} />
              </>
            )}
          </AnimatePresence>
        </div>

        {/* نمودار قیمت طلا */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <GoldChart height={380} />
        </motion.div>

        {/* فوتر */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="text-center mt-16 text-sm text-gray-500 bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/30"
        >
          <p>
            © {new persianDate().format('YYYY')} - سیستم مدیریت طلا | همه حقوق محفوظ است
          </p>
        </motion.div>
      </div>
    </>
  );
}